// src/components/resources/ebooks/EbookCover.tsx

import { cn } from '~/lib/utils'
import { getEbookCoverTheme } from '~/lib/resources/data/ebooks/themes'

type EbookCoverSize = 'sm' | 'md' | 'lg' | 'xl'

interface EbookCoverProps {
  title: string
  category?: string
  author?: string
  pageCount?: number
  size?: EbookCoverSize
  className?: string
  /** When true, adds a subtle 3D perspective + spine shadow */
  elevated?: boolean
}

const SIZE_CLASSES: Record<EbookCoverSize, { shell: string; title: string; meta: string; glyph: string }> = {
  sm: { shell: 'w-[88px]', title: 'text-[9px] leading-tight', meta: 'text-[7px]', glyph: 'text-lg' },
  md: { shell: 'w-[140px]', title: 'text-xs leading-snug', meta: 'text-[9px]', glyph: 'text-2xl' },
  lg: { shell: 'w-[200px]', title: 'text-sm leading-snug', meta: 'text-[10px]', glyph: 'text-3xl' },
  xl: { shell: 'w-full max-w-[280px]', title: 'text-base leading-snug', meta: 'text-xs', glyph: 'text-4xl' },
}

/**
 * CSS-rendered book cover used on catalog cards and detail heroes.
 * Avoids shipping per-title raster art for the pilot while giving each
 * category a distinct, polished visual identity.
 */
export function EbookCover({
  title,
  category,
  author = 'AdSights',
  pageCount,
  size = 'md',
  className,
  elevated = true,
}: EbookCoverProps): JSX.Element {
  const theme = getEbookCoverTheme(category)
  const sizing = SIZE_CLASSES[size]

  return (
    <div
      className={cn(
        'relative aspect-[2/3] select-none',
        sizing.shell,
        elevated && 'motion-safe:transition-transform motion-safe:duration-300 motion-safe:group-hover:-translate-y-1',
        className,
      )}
      aria-hidden="true"
    >
      {/* Spine + depth */}
      {elevated && (
        <>
          <div
            className="absolute -left-1.5 bottom-1 top-3 w-3 rounded-l-sm bg-gradient-to-r from-black/40 to-black/10 dark:from-black/60"
            style={{ transform: 'skewY(-2deg)' }}
          />
          <div className="absolute -inset-1 -z-10 rounded-r-xl bg-black/10 blur-md dark:bg-black/30" />
        </>
      )}

      {/* Cover face */}
      <div
        className={cn(
          'relative flex h-full flex-col overflow-hidden rounded-r-lg rounded-l-sm border border-white/10 bg-gradient-to-br shadow-lg',
          theme.gradient,
        )}
      >
        {/* Top brand strip */}
        <div className="bg-black/20 px-3 py-2 backdrop-blur-sm">
          <p className="text-[8px] font-semibold uppercase tracking-[0.2em] text-white/80 sm:text-[9px]">
            AdSights
          </p>
          <p className="mt-0.5 text-[7px] font-medium text-white/60 sm:text-[8px]">{theme.label}</p>
        </div>

        {/* Center glyph */}
        <div className="flex flex-1 flex-col items-center justify-center px-3 py-4">
          <span className={cn('text-white/30', sizing.glyph)}>{theme.glyph}</span>
        </div>

        {/* Title block */}
        <div className="space-y-1.5 bg-gradient-to-t from-black/50 to-transparent px-3 pb-3 pt-6">
          <p className={cn('font-bold text-white line-clamp-3', sizing.title)}>{title}</p>
          <div className={cn('flex items-center justify-between text-white/70', sizing.meta)}>
            <span>{author}</span>
            {pageCount && <span>{pageCount} pp</span>}
          </div>
        </div>

        {/* Page edge highlight */}
        <div className="pointer-events-none absolute inset-y-2 right-0 w-px bg-white/20" />
        <div className="pointer-events-none absolute inset-y-3 right-0.5 w-px bg-white/10" />
      </div>
    </div>
  )
}
